import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Input, Popconfirm, Select, Slider } from 'antd';
import { resolveClientCatalog } from './expressionCatalog';
import { writesStayLocal } from './localOnly';
import { exportSocialClip, playCharacterAnimation, playSceneAnimation, previewCharacterAnimation, updateCharacter } from './api';
import { GenerationThumb, ImageGallery } from './GenerationGallery';
import { accordionKey, useAccordionOpen } from './accordionState';
import { clipLabel, clipsForPrompt, groupAnimationYears } from './animationLibrary';
import { enqueuePictureUrls, usePictureUrl } from './pictureLoad';
import { DOG_TRICKS, PERSON_ACTIVITY_POSES, vanShowsFlight } from './characterPoses';
import './AnimationStudio.css';

const { TextArea } = Input;

const workTitleStyle = {
  margin: 0,
  flex: '1 1 auto',
  minWidth: 0,
  fontFamily: 'var(--font-display)',
  fontSize: 'var(--text-body)',
  fontWeight: 'var(--font-weight-semibold)',
  letterSpacing: 'var(--tracking-title)',
  textWrap: 'balance',
};

function isArchived(item) {
  return item?.status === 'archived' || Boolean(item?.archivedAt) || item?.archived;
}

function hasPicture(generation) {
  if (!generation || generation.error) return false;
  return Boolean(
    generation.output?.imageUrl ||
      generation.output?.path ||
      generation.output?.contentUrl ||
      generation.storagePath
  );
}

function isLocked(generation) {
  return hasPicture(generation) && Boolean(generation.lockedAsReference || generation.approved);
}

function displayName(character) {
  const title = String(character?.title || character?.name || character?.id || 'Character');
  const cut = title.split(':')[0].trim();
  return cut || title;
}

function showsFace(character) {
  const id = String(character?.id || '').toLowerCase();
  return !/(^|-)(van|tree)(-|$)/.test(id);
}

function catalogKind(character) {
  const blob = `${character?.id || ''} ${character?.title || ''}`.toLowerCase();
  if (/(^|[^a-z])(dog|mutt)([^a-z]|$)/.test(blob)) return 'dog';
  return 'person';
}

function emotionRows(character) {
  const cells = resolveClientCatalog(null, catalogKind(character), character?.id);
  const byEmotion = new Map();
  for (const cell of cells) {
    if (!byEmotion.has(cell.emotion)) byEmotion.set(cell.emotion, []);
    byEmotion.get(cell.emotion).push(cell);
  }
  return [...byEmotion.entries()].map(([emotion, row]) => ({
    emotion,
    label: emotion.charAt(0).toUpperCase() + emotion.slice(1),
    cells: row,
  }));
}

function imageSrc(projectId, generation) {
  if (!generation?.output || generation.error) return '';
  if (generation.output.imageUrl) return generation.output.imageUrl;
  if (!writesStayLocal()) return '';
  if (generation.output.contentUrl) return generation.output.contentUrl;
  if (generation.output.path) return `/content/projects/${projectId}/${generation.output.path}`;
  return '';
}

function firstFaceLabel(generation) {
  const match = String(generation?.prompt || '').match(/\b1\)\s*([^—–\n]+)/);
  return match ? match[1].trim() : '';
}

function SheetOption({ projectId, generation, title }) {
  const loaded = usePictureUrl(projectId, generation);
  const src = imageSrc(projectId, generation) || (loaded && loaded !== 'failed' ? loaded : '');
  return (
    <span className="animation-sheet-option">
      {src ? <img src={src} alt="" /> : null}
      <span>{title}</span>
    </span>
  );
}

function sheetChoice(projectId, sheet) {
  return {
    value: sheet.id,
    label: <SheetOption projectId={projectId} generation={sheet.generation} title={sheet.title} />,
  };
}

function lockedEmotionSheets(projectId, character, generations) {
  const rows = showsFace(character) ? emotionRows(character) : [];
  const byKey = new Map();
  for (const generation of generations || []) {
    if (!isLocked(generation) || generation.characterId !== character?.id) continue;
    if (generation.target !== 'expression-row') continue;
    const first = firstFaceLabel(generation);
    const match = rows.find((item) => item.cells[0]?.label === first);
    const key = match?.emotion || first || generation.id;
    const sheet = {
      id: generation.id,
      title: match?.label || first || 'Emotion',
      row: match || null,
      src: imageSrc(projectId, generation),
      generation,
      createdAt: generation.createdAt || '',
    };
    const prev = byKey.get(key);
    if (!prev || String(sheet.createdAt) > String(prev.createdAt)) byKey.set(key, sheet);
  }
  const ordered = [];
  for (const item of rows) {
    const sheet = byKey.get(item.emotion);
    if (sheet) ordered.push(sheet);
  }
  for (const sheet of byKey.values()) {
    if (!ordered.some((item) => item.id === sheet.id)) ordered.push(sheet);
  }
  return ordered;
}

function hasLockedPose(character, generations, pose) {
  const want = String(pose || '').trim().toLowerCase();
  if (!want) return false;
  return (generations || []).some(
    (generation) =>
      isLocked(generation) &&
      generation.characterId === character?.id &&
      generation.target === 'character' &&
      String(generation.pose || '').trim().toLowerCase() === want
  );
}

function activityPoses(character) {
  if (catalogKind(character) === 'dog') return DOG_TRICKS.map((pose) => pose.label);
  return PERSON_ACTIVITY_POSES.map((pose) => pose.label);
}

function WorkHeader({ id, title, titleTag: TitleTag = 'h3', open, panelId, onToggle }) {
  return (
    <div className="era-card__top">
      <TitleTag id={id} style={workTitleStyle}>
        {title}
      </TitleTag>
      <button
        type="button"
        className="era-card__toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        {open ? 'Close' : 'Open'}
      </button>
    </div>
  );
}

function useWorkRunner(onGenerated) {
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState('');
  const activeRun = useRef(0);

  async function run(key, task) {
    if (activeRun.current) return null;
    const id = ++activeRun.current;
    setBusy(key);
    setNotice(null);
    setError('');
    try {
      return await task();
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      if (activeRun.current === id) {
        activeRun.current = 0;
        setBusy('');
      }
    }
  }

  function succeed(data, text) {
    onGenerated?.(data.generation);
    setNotice(text);
  }

  return { busy, notice, error, run, succeed };
}

function PromptGallery({ projectId, clips, character, onDeleteGeneration, deletingId }) {
  const labelFor = (clip) => clipLabel(clip, [character].filter(Boolean));
  return (
    <div className="animation-prompt__gallery">
      {clips.length === 0 ? (
        <p>No clips yet.</p>
      ) : (
        <ImageGallery>
          <ul className="era-scene-gallery">
            {clips.map((clip) => (
              <li key={clip.id} className="era-scene-gallery__item">
                <GenerationThumb
                  projectId={projectId}
                  generation={clip}
                  alt={labelFor(clip)}
                  caption={labelFor(clip)}
                  className="generation-thumb--gallery"
                />
                <Popconfirm
                  title="Delete this clip?"
                  okText="Yes, Delete"
                  cancelText="No, cancel"
                  okButtonProps={{ danger: true, size: 'small' }}
                  cancelButtonProps={{ size: 'small' }}
                  onConfirm={() => onDeleteGeneration?.(clip)}
                >
                  <Button danger loading={deletingId === clip.id}>
                    Delete
                  </Button>
                </Popconfirm>
              </li>
            ))}
          </ul>
        </ImageGallery>
      )}
    </div>
  );
}

const PROMPT_META = {
  emotion: { kind: 'emotion-row', title: 'Emotion', busy: 'emotion' },
  body: { kind: 'body', title: 'Full body', busy: 'body' },
  activity: { kind: 'character-action', title: 'Activity', busy: 'action' },
  'top-down': { kind: 'drive', title: 'Top Down', busy: 'top-down', pose: 'on the road top down' },
  'top-up': { kind: 'drive', title: 'Top Up', busy: 'top-up', pose: 'on the road' },
  flight: { kind: 'flight', title: 'In Flight', busy: 'flight', pose: 'in flight' },
  driving: { kind: 'drive', title: 'Driving', busy: 'driving', pose: 'driving', locks: ['driving', 'on the road'] },
  liftoff: { kind: 'liftoff', title: 'Liftoff', busy: 'liftoff', pose: 'liftoff', startsFrom: 'side view, parked' },
  'sheet-flight': { kind: 'flight', title: 'Flight', busy: 'flight', pose: 'flight', locks: ['flight', 'in flight'] },
};

function promptSaveKey(prompt, pickKey) {
  return `${prompt}:${pickKey}`;
}

function savedAnimationPrompt(character, key) {
  const value = character?.meta?.animationPrompts?.[key];
  return typeof value === 'string' ? value : null;
}

function PromptBox({ title, open, text, onToggle, onText, onSave, saving, dirty, note }) {
  return (
    <>
      <Button type="link" size="small" onClick={onToggle} style={{ paddingInline: 0 }}>
        {open ? 'Hide prompt' : 'Show prompt'}
      </Button>
      {open ? (
        <div className="animation-field">
          <p>Generate follows this.</p>
          <TextArea
            value={text}
            onChange={(event) => onText(event.target.value)}
            rows={8}
            aria-label={`${title} prompt`}
            style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-body-sm)' }}
          />
          <Button onClick={onSave} loading={saving} disabled={!dirty || saving}>
            Save prompt
          </Button>
          {note ? <p>{note}</p> : null}
        </div>
      ) : null}
    </>
  );
}

function FrameChoice({ value, onChange }) {
  return (
    <div role="group" aria-label="Frame" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
      {['9:16', '16:9'].map((ratio) => (
        <Button
          key={ratio}
          type={value === ratio ? 'primary' : 'default'}
          aria-pressed={value === ratio}
          onClick={() => onChange(ratio)}
        >
          {ratio}
        </Button>
      ))}
    </div>
  );
}

function EmotionPick({ projectId, name, emotionSheets, sheetId, onChange }) {
  if (emotionSheets.length === 0) {
    return <p>{name} has no locked emotion sheet. Lock one on Characters.</p>;
  }
  return (
    <label className="animation-field">
      <span>Emotion</span>
      <Select
        value={sheetId || undefined}
        onChange={onChange}
        options={emotionSheets.map((sheet) => sheetChoice(projectId, sheet))}
      />
    </label>
  );
}

function PromptWork({
  projectId,
  character,
  generations,
  onGenerated,
  onDeleteGeneration,
  deletingId,
  prompt,
  emotionSheets,
  onCharacterUpdated,
}) {
  const meta = PROMPT_META[prompt];
  const kind = meta.kind;
  const title = meta.title;
  const poses = activityPoses(character);
  const [open, setOpen] = useAccordionOpen(accordionKey('animation-prompt', projectId, `${character.id}:${prompt}`));
  const [pose, setPose] = useState(poses[0] || '');
  const [action, setAction] = useState(catalogKind(character) === 'dog' ? (poses[0] || '') : '');
  const [manner, setManner] = useState('');
  const [result, setResult] = useState('');
  const [sheetId, setSheetId] = useState(() => emotionSheets[0]?.id || '');
  const [expressionId, setExpressionId] = useState('');
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [loadedText, setLoadedText] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [promptNote, setPromptNote] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const { busy, notice, error, run, succeed } = useWorkRunner(onGenerated);
  const headingId = `animation-prompt-title-${character.id}-${prompt}`;
  const panelId = `animation-prompt-panel-${character.id}-${prompt}`;
  const name = displayName(character);
  const sheet = emotionSheets.find((item) => item.id === sheetId) || null;
  const emotion = sheet?.row?.emotion || '';
  const rowCells = sheet?.row?.cells || [];
  const pickedCell = rowCells.find((cell) => cell.id === expressionId) || null;
  const activityLock = catalogKind(character) === 'dog' ? 'standing' : 'full body';
  const vanPose = meta.pose || '';
  const lockedPose = prompt === 'body'
    ? 'standing'
    : prompt === 'activity'
      ? activityLock
      : vanPose || pose;
  const poseLocked = (meta.locks || [lockedPose]).some((label) => hasLockedPose(character, generations, label))
    && (!meta.startsFrom || hasLockedPose(character, generations, meta.startsFrom));
  const clips = useMemo(
    () => clipsForPrompt(generations, { characterId: character.id, kind, pose: vanPose }),
    [generations, character.id, kind, vanPose]
  );
  const pickKey = prompt === 'activity'
    ? `${emotion}\n${pose}`
    : prompt === 'emotion'
      ? `${sheet?.id || ''}\n${emotion}`
      : prompt === 'body'
        ? (pickedCell ? `${emotion}\n${pickedCell.id}` : emotion)
        : prompt;
  const saveKey = promptSaveKey(prompt, pickKey);
  const previewRequest = useRef(0);
  const promptTextRef = useRef('');
  const previewArgs = useRef({});
  const statementAtOpen = useRef(`${action}\n${manner}\n${result}`);
  promptTextRef.current = promptText;
  previewArgs.current = {
    projectId,
    characterId: character.id,
    kind,
    prompt,
    emotion,
    expressionId: pickedCell?.id || '',
    sheetId: sheet?.id,
    pose: meta.pose || '',
    action,
    manner,
    result,
  };
  const previewBlocksGenerate = Boolean(previewError) && promptText === '';

  function loadPreview() {
    const requestId = ++previewRequest.current;
    const args = previewArgs.current;
    const body = { characterId: args.characterId, kind: args.kind };
    if (args.prompt === 'emotion') {
      body.emotion = args.emotion;
      body.sheetId = args.sheetId;
    } else if (args.prompt === 'body' || args.prompt === 'activity') {
      body.emotion = args.emotion;
      if (args.prompt === 'body' && args.expressionId) body.expressionId = args.expressionId;
      if (args.prompt === 'activity') {
        body.statement = {
          action: String(args.action || '').trim(),
          manner: String(args.manner || '').trim(),
          result: String(args.result || '').trim(),
        };
      }
    } else if (args.pose) {
      body.pose = args.pose;
    }
    previewCharacterAnimation(args.projectId, body)
      .then((data) => {
        if (previewRequest.current !== requestId) return;
        const next = typeof data?.prompt === 'string' ? data.prompt : '';
        setPromptText(next);
        setLoadedText(next);
        setPromptNote('');
        setPreviewError('');
      })
      .catch((err) => {
        if (previewRequest.current !== requestId) return;
        setPreviewError(err?.message || 'The prompt could not be loaded.');
      });
    return () => {
      if (previewRequest.current === requestId) previewRequest.current += 1;
    };
  }

  useEffect(() => {
    if (!emotionSheets.some((item) => item.id === sheetId)) {
      setSheetId(emotionSheets[0]?.id || '');
    }
  }, [emotionSheets, sheetId]);

  function applySavedPrompt(text) {
    previewRequest.current += 1;
    setPromptText(text);
    setLoadedText(text);
    setPromptNote('');
    setPreviewError('');
  }

  useEffect(() => {
    const saved = savedAnimationPrompt(character, saveKey);
    if (saved != null) {
      applySavedPrompt(saved);
      return undefined;
    }
    if (!promptOpen && promptTextRef.current === '') return undefined;
    return loadPreview();
    // A saved prompt stays. Otherwise refill when the face sheet, emotion, activity pose, or Drive versus Flight changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickKey, saveKey]);

  useEffect(() => {
    if (!promptOpen) return undefined;
    if (promptTextRef.current !== '') return undefined;
    const saved = savedAnimationPrompt(character, saveKey);
    if (saved != null) {
      applySavedPrompt(saved);
      return undefined;
    }
    return loadPreview();
    // Opening fetches only when the box is empty, so a reopen does not replace an edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptOpen]);

  useEffect(() => {
    if (prompt !== 'activity' || !promptOpen) return undefined;
    if (savedAnimationPrompt(character, saveKey) != null) return undefined;
    const statementKey = `${action}\n${manner}\n${result}`;
    if (statementKey === statementAtOpen.current) return undefined;
    const timer = setTimeout(() => {
      statementAtOpen.current = statementKey;
      loadPreview();
    }, 300);
    return () => clearTimeout(timer);
    // Refill the activity sentence after a short pause, and only while the box is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt, promptOpen, action, manner, result]);

  function editPrompt(value) {
    setPromptText(value);
    setPromptNote('');
  }

  async function savePrompt() {
    previewRequest.current += 1;
    setSavingPrompt(true);
    setPromptNote('');
    const animationPrompts = { ...(character.meta?.animationPrompts || {}) };
    if (promptText.trim()) animationPrompts[saveKey] = promptText;
    else delete animationPrompts[saveKey];
    try {
      const data = await updateCharacter(projectId, character.id, { meta: { animationPrompts } });
      onCharacterUpdated?.(data);
      setLoadedText(promptText);
      setPromptNote('Saved.');
    } catch (err) {
      setPromptNote(err?.message || 'The prompt could not be saved.');
    } finally {
      setSavingPrompt(false);
    }
  }

  function choosePose(label) {
    setPose(label);
    if (catalogKind(character) === 'dog') setAction(label);
  }

  function togglePrompt() {
    setPromptOpen((value) => !value);
  }

  async function play() {
    if (prompt === 'top-down' || prompt === 'top-up' || prompt === 'flight' || prompt === 'driving' || prompt === 'liftoff' || prompt === 'sheet-flight') {
      const data = await run(meta.busy, () =>
        playCharacterAnimation(projectId, {
          characterId: character.id,
          kind,
          pose: meta.pose,
          prompt: promptText,
          aspectRatio,
        })
      );
      if (data) succeed(data, `${name}'s ${title.toLowerCase()} clip is ready.`);
      return;
    }
    const payload = {
      characterId: character.id,
      kind,
      sheetId: sheet?.id,
      emotion,
      prompt: promptText,
      ...(prompt === 'body' && pickedCell ? { expressionId: pickedCell.id } : {}),
    };
    if (prompt === 'activity') {
      const data = await run(meta.busy, () =>
        playCharacterAnimation(projectId, {
          ...payload,
          pose,
          statement: { action: action.trim(), manner: manner.trim(), result: result.trim() },
        })
      );
      if (data) succeed(data, `${name} is ready.`);
      return;
    }
    const data = await run(meta.busy, () => playCharacterAnimation(projectId, payload));
    if (data) {
      succeed(
        data,
        prompt === 'body'
          ? `${name}'s ${pickedCell?.label || sheet?.title || 'full body'} clip is ready.`
          : `${name}'s ${sheet?.title || 'face'} row is ready.`
      );
    }
  }

  return (
    <section className="animation-panel" aria-labelledby={headingId}>
      <WorkHeader
        id={headingId}
        title={title}
        titleTag="h4"
        open={open}
        panelId={panelId}
        onToggle={() => setOpen((value) => !value)}
      />
      {open ? (
        <div id={panelId} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {error ? <Alert type="error" showIcon title={error} /> : null}
          {previewError ? <Alert type="error" showIcon title={previewError} /> : null}
          {notice ? <Alert type="success" showIcon title={notice} /> : null}
          <div className="animation-prompt__work">
            <PromptGallery
              projectId={projectId}
              clips={clips}
              character={character}
              onDeleteGeneration={onDeleteGeneration}
              deletingId={deletingId}
            />
            <div
              className="animation-prompt__form"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--space-3)' }}
            >
              {prompt === 'emotion' ? (
                <>
                  <p>
                    Head only. Uses the first head on this sheet. The face travels the row. The camera stays on that head.
                  </p>
                  <EmotionPick
                    projectId={projectId}
                    name={name}
                    emotionSheets={emotionSheets}
                    sheetId={sheetId}
                    onChange={setSheetId}
                  />
                  <PromptBox
                    title={title}
                    open={promptOpen}
                    text={promptText}
                    onToggle={togglePrompt}
                    onText={editPrompt}
                    onSave={savePrompt}
                    saving={savingPrompt}
                    dirty={promptText !== loadedText}
                    note={promptNote}
                  />
                  <Button type="primary" loading={busy === 'emotion'} disabled={!emotion || previewBlocksGenerate} onClick={play}>
                    Generate
                  </Button>
                </>
              ) : null}
              {prompt === 'body' ? (
                <>
                  <p>
                    {pickedCell
                      ? 'Uses the locked standing still. The figure moves into the expression you pick, then back out. The whole figure stays in frame.'
                      : 'Uses the locked standing still. The face and the body travel the emotion you pick. The whole figure stays in frame.'}
                  </p>
                  {!poseLocked ? <p>Lock a standing still on Characters.</p> : null}
                  <EmotionPick
                    projectId={projectId}
                    name={name}
                    emotionSheets={emotionSheets}
                    sheetId={sheetId}
                    onChange={setSheetId}
                  />
                  <label className="animation-field">
                    <span>Expression</span>
                    <Select
                      value={pickedCell ? pickedCell.id : '__row__'}
                      onChange={(value) => setExpressionId(value === '__row__' ? '' : value)}
                      options={[
                        { value: '__row__', label: 'Whole row' },
                        ...rowCells.map((cell) => ({ value: cell.id, label: cell.label })),
                      ]}
                    />
                  </label>
                  <PromptBox
                    title={title}
                    open={promptOpen}
                    text={promptText}
                    onToggle={togglePrompt}
                    onText={editPrompt}
                    onSave={savePrompt}
                    saving={savingPrompt}
                    dirty={promptText !== loadedText}
                    note={promptNote}
                  />
                  <Button type="primary" loading={busy === 'body'} disabled={!poseLocked || !emotion || previewBlocksGenerate} onClick={play}>
                    Generate full body
                  </Button>
                </>
              ) : null}
              {prompt === 'activity' ? (
                <>
                  <p>
                    {activityLock === 'standing'
                      ? 'Uses the locked standing still. The action continues while the face and the body travel the emotion you pick.'
                      : 'Uses the locked full body still. The action continues while the face and the body travel the emotion you pick.'}
                  </p>
                  <label className="animation-field">
                    <span>Activity</span>
                    <Select
                      value={pose || undefined}
                      onChange={choosePose}
                      options={poses.map((label) => ({ value: label, label }))}
                    />
                  </label>
                  {!poseLocked ? (
                    <p>
                      {activityLock === 'standing'
                        ? 'Lock a standing still on Characters.'
                        : 'Lock a full body still on Characters.'}
                    </p>
                  ) : null}
                  <EmotionPick
                    projectId={projectId}
                    name={name}
                    emotionSheets={emotionSheets}
                    sheetId={sheetId}
                    onChange={setSheetId}
                  />
                  <fieldset className="animation-block" disabled={!poseLocked}>
                    <legend>If this, then that</legend>
                    <label className="animation-field">
                      <span>Action</span>
                      <Input
                        value={action}
                        onChange={(event) => setAction(event.target.value)}
                        placeholder="chopping wood"
                      />
                    </label>
                    <label className="animation-field">
                      <span>Manner</span>
                      <Input
                        value={manner}
                        onChange={(event) => setManner(event.target.value)}
                        placeholder="earnestly"
                      />
                    </label>
                    <label className="animation-field">
                      <span>Result</span>
                      <Input
                        value={result}
                        onChange={(event) => setResult(event.target.value)}
                        placeholder="stacks wood along a cord"
                      />
                    </label>
                  </fieldset>
                  <PromptBox
                    title={title}
                    open={promptOpen}
                    text={promptText}
                    onToggle={togglePrompt}
                    onText={editPrompt}
                    onSave={savePrompt}
                    saving={savingPrompt}
                    dirty={promptText !== loadedText}
                    note={promptNote}
                  />
                  <Button
                    type="primary"
                    loading={busy === 'action'}
                    disabled={!poseLocked || !emotion || !action.trim() || !result.trim() || previewBlocksGenerate}
                    onClick={play}
                  >
                    Generate
                  </Button>
                </>
              ) : null}
              {prompt === 'top-down' || prompt === 'top-up' || prompt === 'flight' || prompt === 'driving' || prompt === 'liftoff' || prompt === 'sheet-flight' ? (
                <>
                  <p>
                    {prompt === 'top-down'
                      ? 'Uses the locked top-down road still. The van stays centered and faces right, on the road beside a lake, with mountains beyond. The roof stays down. The road moves under it.'
                      : prompt === 'top-up'
                        ? 'Uses the locked road still. The van stays centered and faces right, on the road beside a lake, with mountains beyond. The road moves under it.'
                        : prompt === 'driving'
                          ? 'Uses the locked driving still. The van stays centered and faces right, on the road beside a lake, with mountains beyond. The wings stay in the driving position. The road moves under it.'
                          : prompt === 'liftoff'
                            ? 'Starts from the locked side view, parked still, then moves into the locked liftoff illustration.'
                            : 'Uses the locked flight still. The van flies over mountains and lakes. The tires stay tucked and the engines stay on.'}
                  </p>
                  {!poseLocked ? (
                    <p>
                      {prompt === 'top-down'
                        ? 'Lock a top-down road still on Characters.'
                        : prompt === 'top-up'
                          ? 'Lock a road still on Characters.'
                          : prompt === 'driving'
                            ? 'Lock a driving still on Characters.'
                            : prompt === 'liftoff'
                              ? 'Lock a side view, parked still and a liftoff still on Characters.'
                              : 'Lock the flight still on Characters.'}
                    </p>
                  ) : null}
                  <FrameChoice value={aspectRatio} onChange={setAspectRatio} />
                  <PromptBox
                    title={title}
                    open={promptOpen}
                    text={promptText}
                    onToggle={togglePrompt}
                    onText={editPrompt}
                    onSave={savePrompt}
                    saving={savingPrompt}
                    dirty={promptText !== loadedText}
                    note={promptNote}
                  />
                  <Button type="primary" loading={busy === meta.busy} disabled={!poseLocked || previewBlocksGenerate} onClick={play}>
                    Generate
                  </Button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function CharacterWork({
  projectId,
  character,
  generations,
  onGenerated,
  onDeleteGeneration,
  deletingId,
  onCharacterUpdated,
}) {
  const [open, setOpen] = useAccordionOpen(accordionKey('animation-character', projectId, character.id));
  const headingId = `animation-character-title-${character.id}`;
  const panelId = `animation-character-panel-${character.id}`;
  const emotionSheets = useMemo(
    () => lockedEmotionSheets(projectId, character, generations),
    [projectId, character, generations]
  );

  useEffect(() => {
    if (!open) return;
    enqueuePictureUrls(
      projectId,
      (generations || []).filter(
        (generation) =>
          isLocked(generation)
          && generation.characterId === character.id
          && generation.target === 'expression-row'
      )
    );
  }, [open, projectId, character.id, generations]);

  return (
    <section className="animation-panel" aria-labelledby={headingId}>
      <WorkHeader
        id={headingId}
        title={displayName(character)}
        open={open}
        panelId={panelId}
        onToggle={() => setOpen((value) => !value)}
      />
      {open ? (
        <div id={panelId} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {showsFace(character) ? (
            <>
              <PromptWork
                key="emotion"
                prompt="emotion"
                projectId={projectId}
                character={character}
                generations={generations}
                onGenerated={onGenerated}
                onDeleteGeneration={onDeleteGeneration}
                deletingId={deletingId}
                onCharacterUpdated={onCharacterUpdated}
                emotionSheets={emotionSheets}
              />
              <PromptWork
                key="body"
                prompt="body"
                projectId={projectId}
                character={character}
                generations={generations}
                onGenerated={onGenerated}
                onDeleteGeneration={onDeleteGeneration}
                deletingId={deletingId}
                onCharacterUpdated={onCharacterUpdated}
                emotionSheets={emotionSheets}
              />
              <PromptWork
                key="activity"
                prompt="activity"
                projectId={projectId}
                character={character}
                generations={generations}
                onGenerated={onGenerated}
                onDeleteGeneration={onDeleteGeneration}
                deletingId={deletingId}
                onCharacterUpdated={onCharacterUpdated}
                emotionSheets={emotionSheets}
              />
            </>
          ) : character.meta?.kind === 'van' && character.meta?.viewPrompts ? (
            <>
              <PromptWork
                key="driving"
                prompt="driving"
                projectId={projectId}
                character={character}
                generations={generations}
                onGenerated={onGenerated}
                onDeleteGeneration={onDeleteGeneration}
                deletingId={deletingId}
                onCharacterUpdated={onCharacterUpdated}
                emotionSheets={emotionSheets}
              />
              <PromptWork
                key="liftoff"
                prompt="liftoff"
                projectId={projectId}
                character={character}
                generations={generations}
                onGenerated={onGenerated}
                onDeleteGeneration={onDeleteGeneration}
                deletingId={deletingId}
                onCharacterUpdated={onCharacterUpdated}
                emotionSheets={emotionSheets}
              />
              <PromptWork
                key="sheet-flight"
                prompt="sheet-flight"
                projectId={projectId}
                character={character}
                generations={generations}
                onGenerated={onGenerated}
                onDeleteGeneration={onDeleteGeneration}
                deletingId={deletingId}
                onCharacterUpdated={onCharacterUpdated}
                emotionSheets={emotionSheets}
              />
            </>
          ) : character.meta?.kind === 'van' ? (
            <>
              <PromptWork
                key="top-down"
                prompt="top-down"
                projectId={projectId}
                character={character}
                generations={generations}
                onGenerated={onGenerated}
                onDeleteGeneration={onDeleteGeneration}
                deletingId={deletingId}
                onCharacterUpdated={onCharacterUpdated}
                emotionSheets={emotionSheets}
              />
              <PromptWork
                key="top-up"
                prompt="top-up"
                projectId={projectId}
                character={character}
                generations={generations}
                onGenerated={onGenerated}
                onDeleteGeneration={onDeleteGeneration}
                deletingId={deletingId}
                onCharacterUpdated={onCharacterUpdated}
                emotionSheets={emotionSheets}
              />
              {vanShowsFlight(character) ? (
                <PromptWork
                  key="flight"
                  prompt="flight"
                  projectId={projectId}
                  character={character}
                  generations={generations}
                  onGenerated={onGenerated}
                  onDeleteGeneration={onDeleteGeneration}
                  deletingId={deletingId}
                  onCharacterUpdated={onCharacterUpdated}
                  emotionSheets={emotionSheets}
                />
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function SceneWork({
  projectId,
  page,
  generations,
  onGenerated,
  onDeleteGeneration,
  deletingId,
}) {
  const [open, setOpen] = useAccordionOpen(accordionKey('animation-scene', projectId, page.id));
  const [shotsText, setShotsText] = useState(page.title || page.scenePrompt || page.id);
  const [startSec, setStartSec] = useState(0);
  const [durationSec, setDurationSec] = useState(6);
  const { busy, notice, error, run, succeed } = useWorkRunner(onGenerated);
  const headingId = `animation-scene-title-${page.id}`;
  const panelId = `animation-scene-panel-${page.id}`;
  const clips = useMemo(
    () => clipsForPrompt(generations, { pageId: page.id, kind: 'scene' }),
    [generations, page.id]
  );
  const scene = clips.find((clip) => clip.kind === 'scene') || null;
  const sceneId = scene?.id || '';
  const sceneSpanSec = Math.max(1, scene?.shotIds?.length || 1) * 8;
  const maxStartSec = Math.max(0, sceneSpanSec - durationSec);

  useEffect(() => {
    setStartSec((current) => (current > maxStartSec ? maxStartSec : current));
  }, [maxStartSec]);

  function changeDuration(nextDuration) {
    const nextMaxStart = Math.max(0, sceneSpanSec - nextDuration);
    setDurationSec(nextDuration);
    setStartSec((current) => Math.min(current, nextMaxStart));
  }

  async function playScene() {
    const shots = shotsText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((text) => ({ text, pageId: page.id }));
    const data = await run('scene', () =>
      playSceneAnimation(projectId, { pageIds: [page.id], shots })
    );
    if (data) succeed(data, 'The scene is ready.');
  }

  async function exportClip() {
    const data = await run('clip', () =>
      exportSocialClip(projectId, sceneId, {
        startSec: Math.min(startSec, maxStartSec),
        durationSec,
      })
    );
    if (data) succeed(data, 'The 9:16 clip is saved on this scene.');
  }

  return (
    <section className="animation-panel" aria-labelledby={headingId}>
      <WorkHeader
        id={headingId}
        title={page.title || page.id}
        open={open}
        panelId={panelId}
        onToggle={() => setOpen((value) => !value)}
      />
      {open ? (
        <div id={panelId} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {error ? <Alert type="error" showIcon title={error} /> : null}
          {notice ? <Alert type="success" showIcon title={notice} /> : null}
          <div className="animation-prompt__work">
            <PromptGallery
              projectId={projectId}
              clips={clips}
              onDeleteGeneration={onDeleteGeneration}
              deletingId={deletingId}
            />
            <div
              className="animation-prompt__form"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--space-3)' }}
            >
              <label className="animation-field">
                <span>Shots, one per line</span>
                <TextArea
                  rows={4}
                  value={shotsText}
                  onChange={(event) => setShotsText(event.target.value)}
                  placeholder="One beat per line"
                />
              </label>
              <Button
                type="primary"
                loading={busy === 'scene'}
                disabled={!shotsText.trim() || Boolean(busy)}
                onClick={playScene}
              >
                Generate scene
              </Button>
              <p>Mark a 4–8 second window. Export saves a separate 9:16 file on this scene.</p>
              <label className="animation-field">
                <span>Start at {Math.min(startSec, maxStartSec)}s</span>
                <Slider
                  min={0}
                  max={maxStartSec}
                  value={Math.min(startSec, maxStartSec)}
                  onChange={setStartSec}
                  disabled={!sceneId}
                />
              </label>
              <label className="animation-field">
                <span>Length {durationSec}s</span>
                <Slider min={4} max={8} value={durationSec} onChange={changeDuration} disabled={!sceneId} />
              </label>
              <Button loading={busy === 'clip'} disabled={!sceneId || Boolean(busy)} onClick={exportClip}>
                Export 9:16 clip
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default function AnimationStudio({
  projectId,
  characters,
  chapters,
  generations,
  onGenerated,
  onDeleteGeneration,
  deletingId,
  onCharacterUpdated,
  showScenes = false,
}) {
  const people = useMemo(
    () => (characters || []).filter((character) => character?.id && !isArchived(character)),
    [characters]
  );
  const lockedPageIds = useMemo(() => {
    const ids = new Set();
    for (const generation of generations || []) {
      if (isLocked(generation) && generation.pageId) ids.add(generation.pageId);
    }
    return ids;
  }, [generations]);
  const years = useMemo(
    () => groupAnimationYears({
      chapters: (chapters || []).filter((chapter) => !isArchived(chapter)),
      characters: people,
      lockedPageIds,
    }),
    [chapters, people, lockedPageIds]
  );

  return (
    <div className="animation-studio">
      <nav className="chapter-jump" aria-label="Jump to year">
        {years.map((year) => (
          <a key={year.chapterId} className="chapter-jump__link" href={`#animation-${year.chapterId}`}>
            {year.label}
          </a>
        ))}
      </nav>
      {years.map((year) => (
        <section
          key={year.chapterId}
          id={`animation-${year.chapterId}`}
          className="chapter-anchor animation-year"
          aria-label={year.label}
        >
          <h2 className="chapter-anchor__title">{year.label}</h2>
          {year.characters.map((character) => (
            <CharacterWork
              key={character.id}
              projectId={projectId}
              character={character}
              generations={generations}
              onGenerated={onGenerated}
              onDeleteGeneration={onDeleteGeneration}
              deletingId={deletingId}
              onCharacterUpdated={onCharacterUpdated}
            />
          ))}
          {showScenes
            ? year.pages.map((page) => (
              <SceneWork
                key={page.id}
                projectId={projectId}
                page={page}
                generations={generations}
                onGenerated={onGenerated}
                onDeleteGeneration={onDeleteGeneration}
                deletingId={deletingId}
              />
            ))
            : null}
        </section>
      ))}
    </div>
  );
}
